import { useQuery } from '@tanstack/react-query'
import { supabase, supabaseReady } from '@/lib/supabase'
import { isRealAthleteId } from './useSetLogs'
import { exerciseKey } from '@/lib/exerciseKey'

export interface LiftBest {
  exerciseId: string
  name: string
  best: number
  /** Best in their earliest logged week, when we have 2+ weeks. */
  earliest: number | null
  /** kg gained since their first session on this lift; null with only one session. */
  gain: number | null
  /** Distinct sessions this lift was trained in, across every program. */
  weeks: number
  /** When the best lift was recorded — lets us say "this month". */
  bestAt: string | null
}

const toNum = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').trim())
  return Number.isFinite(n) ? n : null
}

/**
 * Everything a parent-facing progress report needs: best lifts (with any
 * improvement we can evidence), sessions completed, and how long they've been
 * logging. VALD results are fetched separately by the report page.
 */
export function useAthleteReport(athleteId: string, enabled = true) {
  return useQuery({
    queryKey: ['athlete-report', athleteId],
    enabled: enabled && supabaseReady && isRealAthleteId(athleteId),
    queryFn: async () => {
      const [logsRes, doneRes] = await Promise.all([
        supabase
          .from('set_logs')
          .select('exercise_id,exercise_name,program_id,week,day,weight,logged_at')
          .eq('athlete_id', athleteId)
          .not('weight', 'is', null),
        supabase
          .from('day_completions')
          .select('week,day,completed_at')
          .eq('athlete_id', athleteId),
      ])
      if (logsRes.error) throw logsRes.error
      if (doneRes.error) throw doneRes.error

      const logs = (logsRes.data ?? []) as {
        exercise_id: string; exercise_name: string | null; program_id: string | null
        week: number | null; day: number | null; weight: unknown; logged_at: string
      }[]

      // A session is program + week + day. Week alone is ambiguous: weeks restart
      // at 1 in every program, which merged an old program's Week 1 with the new one's.
      const sessionOf = (l: { program_id: string | null; week: number | null; day: number | null; logged_at: string }) =>
        l.week != null ? `${l.program_id ?? '-'}|${l.week}|${l.day ?? '-'}` : l.logged_at.slice(0, 10)

      // Group by LIFT (matched by name across programs), not by exercise id —
      // ids differ between an old program and a new or re-imported one.
      const byLift = new Map<string, {
        name: string; latestAt: string
        sets: { session: string; w: number; at: string }[]
      }>()
      for (const l of logs) {
        const w = toNum(l.weight)
        if (w == null || w <= 0) continue
        const key = exerciseKey(l.exercise_name) || l.exercise_id || 'unknown'
        const entry = byLift.get(key) ?? { name: l.exercise_name?.trim() || key, latestAt: '', sets: [] }
        entry.sets.push({ session: sessionOf(l), w, at: l.logged_at })
        // Show the lift under the wording most recently used for it.
        if (l.logged_at >= entry.latestAt && l.exercise_name?.trim()) {
          entry.latestAt = l.logged_at
          entry.name = l.exercise_name.trim()
        }
        byLift.set(key, entry)
      }

      const lifts: LiftBest[] = [...byLift.entries()].map(([key, e]) => {
        // Order sessions chronologically by when they were actually trained.
        const firstAt = new Map<string, string>()
        for (const x of e.sets) {
          const cur = firstAt.get(x.session)
          if (!cur || x.at < cur) firstAt.set(x.session, x.at)
        }
        const sessions = [...firstAt.entries()].sort((a, b) => a[1].localeCompare(b[1]))
        const best = Math.max(...e.sets.map((x) => x.w))
        // Earliest time they hit that best — when the PB was actually set.
        const bestAt = e.sets.filter((x) => x.w === best).map((x) => x.at).sort()[0] ?? null
        const firstSession = sessions[0]?.[0]
        const earliest = sessions.length >= 2
          ? Math.max(...e.sets.filter((x) => x.session === firstSession).map((x) => x.w))
          : null
        return {
          exerciseId: key,
          name: e.name,
          best,
          earliest,
          gain: earliest != null ? Math.round((best - earliest) * 100) / 100 : null,
          weeks: sessions.length,
          bestAt,
        }
      })

      // Heaviest first — the lifts a parent will recognise sit at the top.
      lifts.sort((a, b) => b.best - a.best)

      // A "recent PB" is a best that beat an earlier week AND was set lately —
      // that's an improvement we can actually evidence, not just a first entry.
      const cutoff = Date.now() - 30 * 864e5
      const recentPBs = lifts
        .filter((l) => (l.gain ?? 0) > 0 && l.bestAt && new Date(l.bestAt).getTime() >= cutoff)
        .sort((a, b) => (b.gain ?? 0) - (a.gain ?? 0))

      // Sessions actually trained, from the weights they logged. More truthful
      // than day_completions, which only started being ticked recently.
      const sessionsTrained = new Set(logs.map(sessionOf)).size

      const dates = logs.map((l) => l.logged_at).sort()
      return {
        lifts,
        improved: lifts.filter((l) => (l.gain ?? 0) > 0),
        recentPBs,
        exercisesLogged: lifts.length,
        setsLogged: logs.length,
        sessionsTrained,
        sessionsCompleted: (doneRes.data ?? []).length,
        firstLog: dates[0] ?? null,
        lastLog: dates[dates.length - 1] ?? null,
      }
    },
  })
}
